import { ENotAuthNavigation } from '@hts/shared/enums';
import { useGetAverageColorByImageSrc } from '@hts/shared/libs';
import { IMappedSubmenuCategory } from '@hts/shared/types';
import { HtsLink, HtsTypography, HtslIcon } from '@hts/shared/ui';
import { removeComponentProps, throttle } from '@hts/shared/utils';
import React, { HTMLProps, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useImage from 'use-image';
import styles from '../../styles/Header/AllSeasonsDropdown.module.scss';

interface IAllSeasonsDropdownProps {
  label: string;
  categories: IMappedSubmenuCategory[];
  fullWidth?: boolean;
  containerProps?: HTMLProps<HTMLDivElement>;
  disabled?: boolean;
}

interface IRenderCategoryColumn {
  id: number;
  parentId?: number;
  name: string;
  slug: string;
  mediaPath: string | null;
}

const MIN_WIDTH_COLUMN = 130;

export const AllSeasonsDropdown: React.FC<IAllSeasonsDropdownProps> = memo(
  ({ label, categories, fullWidth, containerProps, disabled = false }) => {
    const dropdownRef = useRef<HTMLDivElement>(null);
    const columnsRef = useRef<HTMLDivElement>(null);
    const imageContainerRef = useRef<HTMLDivElement>(null);

    const [isOpen, setOpen] = useState(false);
    const [activeImageSrc, setActiveImageSrc] = useState('');
    const [dropdownOffsetLeft, setDropdownOffsetLeft] = useState(0);
    const [imageContainerWidth, setImageContainerWidth] = useState(0);
    const [columnsWidth, setColumnsWidth] = useState(0);
    const [availableWidth, setAvailableWidth] = useState(window.innerWidth);

    const [activeImage, activeImageStatus] = useImage(activeImageSrc, 'anonymous');

    const averageColor = useGetAverageColorByImageSrc({
      image: activeImage,
      imageStatus: activeImageStatus,
      shouldSkipEmptySrc: true,
    });

    const newContainerProps = removeComponentProps(containerProps, ['className']);
    const containerClassName = [styles.container, containerProps?.className].join(' ');

    const renderData = useMemo((): IRenderCategoryColumn[] => {
      const newCategories: IRenderCategoryColumn[] = [];
      const processed = new Set<number>();

      function processCategory(category: IMappedSubmenuCategory, parentId?: number) {
        if (processed.has(category.id)) return;

        processed.add(category.id);

        const { id, name, slug, fileUrl, categories } = category;

        const newCategory: IRenderCategoryColumn = {
          parentId,
          id,
          name,
          slug,
          mediaPath: fileUrl || '',
        };

        newCategories.push(newCategory);

        if (categories && categories.length > 0) {
          categories.forEach((subCategory) => {
            processCategory(subCategory, id);
          });
        }
      }

      categories.forEach((category) => {
        processCategory(category);
      });

      return newCategories;
    }, [categories]);

    const containerStyles: React.CSSProperties = useMemo(
      () => ({ width: fullWidth ? '100%' : 'max-content', opacity: disabled ? 0.7 : 1 }),
      [fullWidth, disabled]
    );

    const menuStyles: React.CSSProperties = useMemo(() => {
      const result: React.CSSProperties = {};

      result.visibility = isOpen ? 'visible' : 'hidden';
      result.opacity = isOpen ? 1 : 0;
      result.width = 'max-content';

      return result;
    }, [isOpen]);

    const columnsStyle: React.CSSProperties = useMemo(() => {
      const result: React.CSSProperties = {};

      const maxAvailableWidth = Math.floor(availableWidth - dropdownOffsetLeft + 38);
      const totalContentWidth = Math.ceil(columnsWidth + imageContainerWidth);
      const availableColumnsWidth = Math.floor(maxAvailableWidth - imageContainerWidth);

      result.width = totalContentWidth > maxAvailableWidth ? availableColumnsWidth : 'max-content';

      return result;
    }, [availableWidth, columnsWidth, dropdownOffsetLeft, imageContainerWidth]);

    useEffect(() => {
      const updateDimensions = () => {
        const viewportWidth = window.visualViewport?.width;

        setAvailableWidth(viewportWidth ?? window.innerWidth);

        if (dropdownRef.current) {
          const rect = dropdownRef.current.getBoundingClientRect();

          setDropdownOffsetLeft(rect.left);
        }
      };

      const throttledHandleResize = throttle(updateDimensions, 200);

      updateDimensions();

      window.addEventListener('resize', throttledHandleResize);

      return () => {
        window.removeEventListener('resize', throttledHandleResize);
      };
    }, []);

    useEffect(() => {
      if (categories.length === 0) return;

      setImageContainerWidth(imageContainerRef.current?.offsetWidth ?? 0);
      setColumnsWidth(columnsRef.current?.offsetWidth ?? 0);
    }, [categories.length]);

    const onMouseEnter = useCallback(() => {
      setOpen(true);
    }, []);

    const onMouseLeave = useCallback(() => {
      setOpen(false);
    }, []);

    const onTouchStart = useCallback(() => {
      setOpen(true);
    }, []);

    const getParentCategorySlugById = useCallback(
      (parentId: number | undefined) => renderData.find((item) => item.id === parentId)?.slug,
      [renderData]
    );

    const renderCategoryColumn = useCallback(
      (categories: IRenderCategoryColumn[]) => {
        const columns: React.ReactNode[] = [];
        const itemsPerColumns = 7;

        let currentColumn: React.ReactNode[] = [];
        let itemCount = 0;

        const createNewColumn = (key: string) => {
          columns.push(
            <ul key={key} className={styles.column} style={{ minWidth: MIN_WIDTH_COLUMN }}>
              {currentColumn}
            </ul>
          );

          currentColumn = [];
          itemCount = 0;
        };

        categories.forEach((category, index) => {
          const baseKey = `${category.id}-${index}`;

          if (itemCount === itemsPerColumns) {
            createNewColumn(`new-column-${baseKey}`);
          }

          const src = category.mediaPath || '';

          const parentSlug = getParentCategorySlugById(category.parentId);

          const redirectTo = parentSlug
            ? `${ENotAuthNavigation.CATEGORY_OF_CARD}/${parentSlug}?sub=${category.slug}`
            : `${ENotAuthNavigation.CATEGORY_OF_CARD}/${category.slug}`;

          currentColumn.push(
            <li
              key={`${baseKey}-item`}
              className={styles.item}
              onMouseEnter={() => setActiveImageSrc(src)}
              onMouseLeave={() => setActiveImageSrc('')}
              onTouchStart={() => setActiveImageSrc(src)}
            >
              <HtsLink
                redirectTo={redirectTo}
                label={category.name}
                typographyProps={{
                  variant: category.parentId ? 'body2' : 'body1',
                  fontWeight: category.parentId ? '300' : '500',
                }}
                linkProps={{ onClick: () => setOpen(false) }}
              />

              <HtslIcon icon="keyboard-arrow-right" className={styles.arrowRightIcon} />
            </li>
          );

          itemCount++;
        });

        if (currentColumn.length > 0) {
          createNewColumn(`new-column-${currentColumn.length}`);
        }

        return columns;
      },
      [getParentCategorySlugById]
    );

    return (
      <div
        className={containerClassName}
        ref={dropdownRef}
        style={containerStyles}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onTouchStart={onTouchStart}
        {...newContainerProps}
      >
        <HtsTypography variant="body2" fontWeight="500" className={styles.label}>
          {label}
        </HtsTypography>

        <HtslIcon
          icon="keyboard-arrow-down-round"
          className={styles.icon}
          style={{ transform: `rotate(${isOpen ? '-180deg' : '0deg'})` }}
        />

        {Boolean(categories.length) && (
          <div className={styles.menuContainer} style={menuStyles}>
            <div ref={columnsRef} className={styles.categoriesContainer} style={columnsStyle}>
              {renderCategoryColumn(renderData)}
            </div>

            <div
              ref={imageContainerRef}
              className={styles.imageContainer}
              style={{ background: activeImageStatus === 'loaded' ? averageColor : 'transparent' }}
            >
              <img
                src={activeImage?.src}
                loading="eager"
                className={activeImageStatus === 'loaded' ? styles.loadedImage : ''}
              />
            </div>
          </div>
        )}
      </div>
    );
  }
);
